import { FileText } from 'lucide-react';
import LegalPage from '../components/LegalPage';

const sections = [
  {
    id: 'acceptance',
    title: '1. Acceptance of Terms',
    body: (
      <p>
        By accessing or using RoomHora (the &ldquo;Services&rdquo;), you agree
        to these Terms of Service. If you are using the Services on behalf of
        an organization, you represent that you have authority to bind that
        organization to these Terms.
      </p>
    ),
  },
  {
    id: 'what-we-are',
    title: '2. What RoomHora Is',
    body: (
      <>
        <p>
          RoomHora is a listing and discovery platform that connects property
          owners (&ldquo;Owners&rdquo;) with people looking for places to rent
          (&ldquo;Renters&rdquo;). <strong>RoomHora is not a party to any
          rental agreement</strong> between Owners and Renters. We do not own,
          manage, lease, or sublease any of the properties listed.
        </p>
        <p>
          We provide tools to make rental discovery safer and more transparent
          — including verified-owner badges, in-platform messaging, and
          reporting — but the rental relationship is strictly between Owner
          and Renter.
        </p>
      </>
    ),
  },
  {
    id: 'eligibility',
    title: '3. Eligibility',
    body: (
      <p>
        You must be at least 18 years old and legally able to enter into
        binding contracts to use RoomHora. You agree to provide accurate,
        current, and complete information when registering and to keep that
        information up to date.
      </p>
    ),
  },
  {
    id: 'accounts',
    title: '4. Accounts & Security',
    body: (
      <>
        <p>
          You&rsquo;re responsible for keeping your password safe and for
          everything that happens on your account. Notify us immediately at{' '}
          <a href="mailto:security@roomhora.com" className="text-accent hover:underline">
            security@roomhora.com
          </a>{' '}
          if you suspect unauthorized access.
        </p>
      </>
    ),
  },
  {
    id: 'listings',
    title: '5. Listings',
    body: (
      <>
        <p>
          Owners are solely responsible for the accuracy of their listings —
          including price, availability, address, photos, amenities, and legal
          right to rent the property. You warrant that:
        </p>
        <ul className="list-disc pl-5 space-y-2">
          <li>You own or have authority to list the property.</li>
          <li>
            The listing complies with all applicable laws (zoning, short-term
            rental regulations, tax obligations, HOA rules).
          </li>
          <li>The photos and description are genuine and current.</li>
          <li>
            Pricing is transparent with no hidden fees beyond what is
            disclosed in the listing.
          </li>
        </ul>
        <p>
          We reserve the right to remove any listing that violates these
          Terms, our Fair Housing policy, or applicable law.
        </p>
      </>
    ),
  },
  {
    id: 'prohibited',
    title: '6. Prohibited Conduct',
    body: (
      <>
        <p>You agree not to:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Post false, misleading, or fraudulent listings.</li>
          <li>
            Circumvent RoomHora&rsquo;s platform by soliciting off-platform
            payment or communications to avoid fees, reviews, or safety
            controls.
          </li>
          <li>Impersonate any person or organization.</li>
          <li>
            Scrape, reverse-engineer, or otherwise interfere with the
            Services.
          </li>
          <li>Harass, threaten, or discriminate against other users.</li>
          <li>
            Upload content that infringes intellectual property, privacy, or
            publicity rights.
          </li>
          <li>Use the Services for any illegal purpose.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'fair-housing',
    title: '7. Fair Housing',
    body: (
      <p>
        RoomHora is committed to equal housing opportunity. Listings and
        communications must not discriminate based on race, color, national
        origin, religion, sex (including gender identity and sexual
        orientation), disability, or familial status. See our{' '}
        <a href="/fair-housing" className="text-accent hover:underline">
          Fair Housing Policy
        </a>{' '}
        for details.
      </p>
    ),
  },
  {
    id: 'fees',
    title: '8. Fees & Payments',
    body: (
      <p>
        Current fees are published at the time of transaction. We do not
        guarantee specific pricing in the future. All amounts are quoted in
        the currency shown in the listing. Taxes and third-party charges
        (e.g., payment processor fees) may apply.
      </p>
    ),
  },
  {
    id: 'disclaimers',
    title: '9. Disclaimers',
    body: (
      <p>
        The Services are provided on an &ldquo;as is&rdquo; and &ldquo;as
        available&rdquo; basis. To the maximum extent permitted by law,
        RoomHora disclaims all warranties, express or implied — including
        merchantability, fitness for a particular purpose, and non-infringement.
        We do not guarantee that listings are accurate, that properties exist,
        or that the Services will be uninterrupted or error-free.
      </p>
    ),
  },
  {
    id: 'liability',
    title: '10. Limitation of Liability',
    body: (
      <>
        <p>
          To the fullest extent permitted by law, RoomHora is not liable for
          any indirect, incidental, consequential, special, or punitive
          damages arising from or related to your use of the Services.
        </p>
        <p>
          Our total aggregate liability for any claim arising from the
          Services shall not exceed the greater of (a) the fees you paid to
          RoomHora in the 12 months preceding the claim, or (b) USD $100.
        </p>
      </>
    ),
  },
  {
    id: 'indemnification',
    title: '11. Indemnification',
    body: (
      <p>
        You agree to indemnify and hold harmless RoomHora, its affiliates,
        officers, employees, and agents from any claims, losses, or damages
        (including reasonable attorneys&rsquo; fees) arising from your
        listings, content, conduct, or violation of these Terms.
      </p>
    ),
  },
  {
    id: 'termination',
    title: '12. Termination',
    body: (
      <p>
        We may suspend or terminate your account at any time if you violate
        these Terms or pose a risk to other users. You may delete your account
        at any time from <strong>Settings → Account</strong>.
      </p>
    ),
  },
  {
    id: 'governing-law',
    title: '13. Governing Law & Disputes',
    body: (
      <p>
        These Terms are governed by the laws of the jurisdiction in which
        RoomHora is incorporated, without regard to conflicts-of-law
        principles. Any dispute will be resolved by binding arbitration,
        except that either party may bring an action in small-claims court.
      </p>
    ),
  },
  {
    id: 'changes',
    title: '14. Changes',
    body: (
      <p>
        We may update these Terms from time to time. We&rsquo;ll post the
        revised date and — for material changes — notify users via email or
        in-product banner at least 14 days in advance. Continued use after
        changes take effect constitutes acceptance.
      </p>
    ),
  },
  {
    id: 'contact',
    title: '15. Contact',
    body: (
      <p>
        Legal:{' '}
        <a href="mailto:legal@roomhora.com" className="text-accent hover:underline">
          legal@roomhora.com
        </a>
        <br />
        Support:{' '}
        <a href="mailto:roomhora@gmail.com" className="text-accent hover:underline">
          roomhora@gmail.com
        </a>
      </p>
    ),
  },
];

export default function Terms() {
  return (
    <LegalPage
      icon={FileText}
      eyebrow="Legal"
      title="Terms of Service"
      effectiveDate="April 22, 2026"
      summary="The ground rules for using RoomHora. We keep them as short and plain as we can — but this is the legal contract between you and us, so it's worth a read."
      sections={sections}
    />
  );
}
